import React from "react"
import { FormSchema, StyledFormSchema } from "@smartdevis/forms"
import { CatalogRef, NPKCatalogs } from "@smartdevis/server/src/domain"
import { ShelfType, Version } from "@smartdevis/server/src/domainCatalog"
import { Modal } from "@smartdevis/ui/src/Modal"
import { useDebounce } from "@smartdevis/ui/src/hooks/useDebounce"
import { GridContainer } from "@smartdevis/ui/src/utils/common"
import { SMap } from "@smartdevis/utils/src/map"
import { isDefined } from "@smartdevis/utils/src/misc"
import { F0, F1, State } from "@smartdevis/utils/src/types"
import { useClientWidth } from "../../../../hooks/utilityHooks"
import { i18n } from "../../../../services/translations"
import { Authentication } from "../../../../store/authState"
import { CatalogFormAttributeType } from "../../devis-catalogs/devis-catalogs"
import { NPKCatalogSearch } from "../crb-catalog-search"
import { NPKCatalogDetails } from "../crb-catalogs-details"
import { NPKCatalogsWorkdescriptionForm } from "./crb-catalogs-workdescription-form"
import { CatalogItemsPreview } from "../../devis-catalogs/devis-catalogs-preview"


type PickerHelpers<Item, FormPayload> = {
    onSubmit: F1<Item>
    onRemove: F1<Item>
    mkInitial: F1<Item, Partial<FormPayload>>
    attachCatalog: (i: Item, catalogId: string, chapter: NPKCatalogs.NPKChapter) => Item
    mkNewItem: (i: Item) => Item
    mkCopyItem: (i: Item) => Item
    getItemId: (i: Item) => string
    renderItem: F1<Item, React.ReactNode>
    renderHeader: F0<React.ReactNode>
    mkSchema: (item: Item) => FormSchema<FormPayload>
    mkStyledSchema: F0<StyledFormSchema<FormPayload, CatalogFormAttributeType>>
}

export type CrbCatalogPickerMode =
    | State<"browseShelf", { force?: boolean }>
    | State<"browseCatalog", { catalogId: string; force?: boolean }>

export interface Ref extends CatalogRef {
    chapter: string,
    section: string,
    workdescription: string
}


export type CrbCatalogItem = { catalogRef?: Ref | null; }
export type CatalogPickerProps<FormPayload, Item extends CrbCatalogItem> = PickerHelpers<Item, FormPayload> & {
    visible: boolean
    onClose: F0
    itemsToPreview: SMap<Item>
    previewTitle: string
    shelfType: ShelfType
    shelfVersion: Version
    item: Item
    credentials: NPKCatalogs.Credentails
    setCredentials: F1<NPKCatalogs.Credentails>
    auth: Authentication
    navigation: NPKCatalogs.ChapterRef
    setNavigation: F1<NPKCatalogs.ChapterRef>
}

export const NPKCatalogPicker = <FormPayload, Item extends CrbCatalogItem>(p: CatalogPickerProps<FormPayload, Item>) => {
    const { item: initialItem, shelfVersion, shelfType } = p

    const [pickerMode, setPickerMode] = React.useState<CrbCatalogPickerMode>(
        initialItem?.catalogRef
            ? { type: "browseCatalog", catalogId: initialItem.catalogRef.chapter }
            : { type: "browseShelf" }
    )
    const [item, setItem] = React.useState<Item>(initialItem)
    const [noChanges, setNoChanges] = React.useState(true)

    const clientWidth = useClientWidth()

    const save = useDebounce((i: Item) => p.onSubmit(i), 1000)

    const close = () => {
        setPickerMode({ type: "browseShelf" })
        if (!noChanges) p.onSubmit(item)
        p.onClose()
    }

    const addToList = (i: Item) => {
        console.warn('addToList ',i);
        // if (!i.catalogRef) return
        setNoChanges(true)
        // p.onSubmit(i)
        setItem(p.mkCopyItem(i))
    }

    const addNewItem = () => {
        setNoChanges(true)
        setPickerMode({ type: "browseShelf" })
        setItem(p.mkNewItem(item))
    }

    const copyItem = () => {
        setNoChanges(true)
        setItem(p.mkCopyItem(item))
    }

    const update = (i: Item) => {
        setNoChanges(false)
        setItem({ ...i, number: i.catalogRef })
        // save.run(i)
    }

    const remove = (i: Item) => {
        if (p.getItemId(i) === p.getItemId(item)) addNewItem()
        p.onRemove(i)
    }

    const viewOtherItem = (i: Item) => {
        if (!i.catalogRef) return
        if (!noChanges) p.onSubmit(item)
        setPickerMode({ type: "browseCatalog", catalogId: i.catalogRef.chapter })
        setItem(i)
        setNoChanges(true)
    }

    const isEditing = isDefined(item && p.itemsToPreview[p.getItemId(item)])
    const isScreenNarrow = clientWidth < 1280

    React.useEffect(() => {
        p.setCredentials({ ...p.credentials, language: p.navigation.language })
    }, [p.navigation.language])


    React.useEffect(() => {
        p.setNavigation({ ...p.navigation, language: p.navigation.language, version: p.navigation.version })
    }, [p.navigation.language, p.navigation.version])

    return (
        <Modal
            header={i18n("Select position from catalog")}
            size="xl"
            height="80vh"
            visible={p.visible}
            onClose={close}>
            <GridContainer columnsGrid={isScreenNarrow ? [1.5, 3.5] : [1.5, 2.5,1.5, 1.0]} gap="12px" height="100%">
                <NPKCatalogSearch
                    shelfType={shelfType}
                    shelfVersion={shelfVersion}
                    pickerMode={pickerMode}
                    setPickerMode={setPickerMode}
                    selectedCatalogRef={item.catalogRef}
                    onBrowseShelf={() => setPickerMode({ type: "browseShelf", force: true })}
                    onBrowseCatalog={catalog => setPickerMode({ type: "browseCatalog", catalogId: catalog, force: true })}
                    credentials={p.credentials}
                    navigation={p.navigation}
                    setCredentials={p.setCredentials}
                    setNavigation={p.setNavigation}
                />
                <NPKCatalogDetails
                    {...p}
                    onAddToList={addToList}
                    onUpdate={update}
                    isEditing={isEditing}
                    onCopy={copyItem}
                    noChanges={noChanges}
                    onBrowseCatalog={() =>
                        item.catalogRef
                            ? setPickerMode({ type: "browseCatalog", catalogId: item.catalogRef.chapter })
                            : null
                    }
                    catalogId={item.catalogRef?.chapter}
                    item={item}
                    auth={p.auth}
                    navigation={p.navigation}
                    setNavigation={p.setNavigation}
                />
                {/* {isScreenNarrow ? null : (
                    <> */}
                <NPKCatalogsWorkdescriptionForm
                    {...p}
                    onAddToList={addToList}
                    onUpdate={update}
                    isEditing={isEditing}
                    onCopy={copyItem}
                    noChanges={noChanges}
                    onBrowseCatalog={() => item.catalogRef
                        ? setPickerMode({ type: "browseCatalog", catalogId: item.catalogRef.chapter })
                        : null}
                    catalogRef={item.catalogRef}
                    catalogId={item.catalogRef?.chapter}
                    item={item}
                    catalogWorkTitle={p.navigation.workDescriptiom}
                />
                <CatalogItemsPreview
                    renderItem={p.renderItem}
                    renderHeader={p.renderHeader}
                    noChanges={noChanges}
                    isEditing={isEditing}
                    onAddNewItem={addNewItem}
                    onClose={close}
                    items={p.itemsToPreview}
                    getItemId={p.getItemId}
                    currentItem={item}
                    onRemove={remove}
                    onViewItem={viewOtherItem}
                    title={p.previewTitle}
                />
                {/* </>
                )} */}
            </GridContainer>
        </Modal>
    )
}
